/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import Modal from "../../Components/Modal";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { UrlEnum } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import theme from "../../Theme/Theme";

type SparePart = { name: string; price: number | string };

type DeviceRepairModalProps = {
  open: boolean;
  onClose: (savedRepair?: any) => void;
  data: any;
};

const emptyState = {
  _id: null as string | null,
  deviceId: null as string | null,
  repairName: "",
  spareParts: [] as SparePart[],
  labourPrice: "" as number | string,
  notes: "",
};

export default function DeviceRepairModal({
  open,
  onClose,
  data,
}: DeviceRepairModalProps) {
  const [state, setState] = useState({ ...emptyState });
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const notifySuccess = (msg: string) => toast.success(msg);
  const notifyError = (msg: string) => toast.error(msg);

  useEffect(() => {
    getData(UrlEnum.devices)
      .then((res) => setDevices(res.data || []))
      .catch((e) => {
        console.log(e);
      });
  }, []);

  useEffect(() => {
    if (data) {
      setState({
        _id: data._id || null,
        deviceId: data.deviceId ? String(data.deviceId) : null,
        repairName: data.repairName || "",
        spareParts: data.spareParts ? [...data.spareParts] : [],
        labourPrice: data.labourPrice ?? "",
        notes: data.notes || "",
      });
    } else {
      setState({ ...emptyState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function computeTotal(): number {
    const parts = state.spareParts.reduce(
      (sum, p) => sum + (Number(p.price) || 0),
      0
    );
    return parts + (Number(state.labourPrice) || 0);
  }

  function handleAddSparePart() {
    setState({
      ...state,
      spareParts: [...state.spareParts, { name: "", price: "" }],
    });
  }

  function handleSparePartChange(
    index: number,
    field: "name" | "price",
    value: string
  ) {
    const updated = [...state.spareParts];
    updated[index] = { ...updated[index], [field]: value };
    setState({ ...state, spareParts: updated });
  }

  function handleRemoveSparePart(index: number) {
    setState({
      ...state,
      spareParts: state.spareParts.filter((_, i) => i !== index),
    });
  }

  function handleDeviceChange(_: any, device: any) {
    setState({ ...state, deviceId: device ? String(device._id) : null });
  }

  async function handleSave() {
    if (!state.deviceId) {
      notifyError(Vocabulary.selectDevice);
      return;
    }
    setLoading(true);
    const body = {
      deviceId: state.deviceId,
      deviceName: selectedDevice?.deviceName || "",
      repairName: state.repairName,
      spareParts: state.spareParts.map((p) => ({
        name: p.name,
        price: Number(p.price) || 0,
      })),
      labourPrice: Number(state.labourPrice) || 0,
      notes: state.notes,
    };

    const url = state._id
      ? `${UrlEnum.deviceRepairs}/${state._id}`
      : UrlEnum.deviceRepairs;

    postData(url, body)
      .then((res) => {
        notifySuccess(Vocabulary.saveSuccess);
        onClose(res.data);
      })
      .catch(() => {
        notifyError(Vocabulary.saveError);
      })
      .finally(() => setLoading(false));
  }

  const selectedDevice =
    devices.find((d) => String(d._id) === state.deviceId) || null;

  return (
    <>
      <Modal
        title={state._id ? (selectedDevice?.deviceName || data?.deviceName || "") : Vocabulary.newRepair}
        open={open}
        onClose={() => onClose(false)}
        restModalProps={{ maxWidth: "md" }}
      >
        {/* Repair name */}
        <Grid item xs={12}>
          <TextField
            label={Vocabulary.repairName}
            size="small"
            fullWidth
            value={state.repairName}
            onChange={(e) => setState({ ...state, repairName: e.target.value })}
          />
        </Grid>

        <Grid container spacing={2} style={{ marginTop: 4 }}>
          {/* Device selector */}
          <Grid item xs={12}>
            <Autocomplete
              options={devices}
              value={selectedDevice}
              getOptionLabel={(d: any) => d.deviceName || ""}
              isOptionEqualToValue={(option: any, value: any) =>
                String(option._id) === String(value._id)
              }
              onChange={handleDeviceChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Vocabulary.selectDevice}
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Spare parts header */}
          <Grid item xs={12}>
            <Divider style={{ margin: "8px 0 4px" }} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <strong>{Vocabulary.spareParts}</strong>
              <Tooltip title={Vocabulary.addSparePart}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAddSparePart}
                >
                  <AddCircleIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Grid>

          {/* Spare parts rows */}
          {state.spareParts.length === 0 && (
            <Grid item xs={12}>
              <span style={{ color: "#aaa", fontSize: 13 }}>
                {Vocabulary.addSparePart}
              </span>
            </Grid>
          )}
          {state.spareParts.map((part, index) => (
            <Grid item xs={12} key={index}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={7}>
                  <TextField
                    label={Vocabulary.sparePart}
                    size="small"
                    fullWidth
                    value={part.name}
                    onChange={(e) =>
                      handleSparePartChange(index, "name", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={Vocabulary.price}
                    size="small"
                    fullWidth
                    type="number"
                    value={part.price}
                    onChange={(e) =>
                      handleSparePartChange(index, "price", e.target.value)
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">RON</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title={Vocabulary.delete}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveSparePart(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          ))}

          {/* Labour price */}
          <Grid item xs={12}>
            <Divider style={{ margin: "8px 0 4px" }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={Vocabulary.labourPrice}
              size="small"
              fullWidth
              type="number"
              value={state.labourPrice}
              onChange={(e) =>
                setState({ ...state, labourPrice: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">RON</InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Total */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={Vocabulary.totalRepairPrice}
              size="small"
              fullWidth
              value={computeTotal()}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">RON</InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              label={Vocabulary.repairNotes}
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={state.notes}
              onChange={(e) => setState({ ...state, notes: e.target.value })}
            />
          </Grid>

          {/* Save button */}
          <Grid item xs={12} style={{ textAlign: "center", marginTop: 8 }}>
            <Button
              variant="contained"
              size="large"
              style={{ color: theme.palette.whiteColor?.main }}
              onClick={handleSave}
            >
              <SaveIcon />
              &nbsp;&nbsp;{Vocabulary.save}
            </Button>
          </Grid>
        </Grid>
      </Modal>

      {loading && (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      )}
    </>
  );
}
